import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Select, Modal, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { MKTU_CLASSES } from '../constants/mktuClasses';

/** На узких экранах в списке только код класса + первые слова описания (без простыни текста). */
const COMPACT_MQ = '(max-width: 768px)';

/** Превью в строке на широком экране (полный текст — в Tooltip при наведении) */
const WIDE_PREVIEW_WORDS = 6;

const firstWords = (text, maxWords = 2) => {
  const t = (text || '').trim();
  if (!t) return '';
  const words = t.split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) return t;
  return `${words.slice(0, maxWords).join(' ')}…`;
};

const normalize = (s) =>
  (s || '')
    .toString()
    .toLowerCase()
    .replace(/ё/g, 'е')
    .trim();

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const highlight = (text, query) => {
  if (!query) return text;
  const safe = escapeRegExp(normalize(query));
  if (!safe) return text;
  // Подсветка по нормализованной строке, но выводим оригинал
  const parts = text.split(new RegExp(`(${escapeRegExp(query)}|${safe})`, 'gi'));
  return parts.map((part, idx) => {
    const pNorm = normalize(part);
    return pNorm && safe && pNorm === safe ? (
      <mark key={idx}>{part}</mark>
    ) : (
      <React.Fragment key={idx}>{part}</React.Fragment>
    );
  });
};

const codeBadgeStyle = (category) => ({
  display: 'inline-block',
  minWidth: 30,
  textAlign: 'center',
  fontWeight: 600,
  borderRadius: 4,
  padding: '0 6px',
  color: '#fff',
  background: category === 'Услуги' ? '#389e0d' : '#2f54eb',
  marginRight: 8,
});

/** Полное описание класса (как раньше в Tooltip) — для модалки по клику на «i» */
const MktuClassDetailBody = ({ c, searchText }) => {
  const itemsList = c.items ? c.items.split(';').map((i) => i.trim()).filter(Boolean) : [];
  let displayItems = [];
  const totalItems = itemsList.length;

  if (searchText && searchText.trim()) {
    const terms = normalize(searchText).split(/\s+/).filter(Boolean);
    const matchedItems = itemsList.filter((item) => {
      const normalizedItem = normalize(item);
      return terms.some((t) => normalizedItem.includes(t));
    });
    displayItems = matchedItems.length > 0 ? matchedItems.slice(0, 15) : itemsList.slice(0, 10);
  } else {
    displayItems = itemsList.slice(0, 10);
  }

  const itemsPreview = displayItems.join('; ');
  const hasMoreItems = totalItems > displayItems.length;

  return (
    <div style={{ maxHeight: 'min(60vh, 420px)', overflowY: 'auto' }}>
      <div style={{ marginBottom: '8px' }}>
        <strong>Класс {c.label}. {c.category}</strong>
      </div>
      <div style={{ marginBottom: '8px' }}>{highlight(c.description, searchText)}</div>
      {c.belongsTo && (
        <div style={{ marginBottom: '8px', fontSize: '12px', opacity: 0.9 }}>
          <strong>К классу относятся:</strong>
          <br />
          {highlight(c.belongsTo, searchText)}
        </div>
      )}
      {c.notBelongsTo && (
        <div style={{ marginBottom: '8px', fontSize: '12px', opacity: 0.9 }}>
          <strong>К классу не относятся:</strong>
          <br />
          {highlight(c.notBelongsTo, searchText)}
        </div>
      )}
      {itemsPreview && (
        <div style={{ fontSize: '12px', opacity: 0.85 }}>
          <strong>
            {searchText && displayItems.length < totalItems
              ? `Найденные ${c.category === 'Товары' ? 'товары' : 'услуги'}:`
              : c.category === 'Товары'
                ? 'Примеры товаров:'
                : 'Примеры услуг:'}
          </strong>
          <br />
          {highlight(itemsPreview, searchText)}
          {hasMoreItems && ` ... (всего в классе: ${totalItems})`}
        </div>
      )}
    </div>
  );
};

/**
 * rc-select задаёт встроенный bottomLeft с overflow.adjustY — при нехватке места снизу список «открывается вверх».
 * Ключа нет в builtinPlacements → берётся только dropdownAlign (низ, без вертикального flip).
 */
const MKTU_PLACEMENT_HACK = '__mktu_dropdown_bottom__';

const MKTU_DROPDOWN_ALIGN = {
  points: ['tl', 'bl'],
  offset: [0, 4],
  overflow: {
    adjustX: 1,
    adjustY: false,
  },
  htmlRegion: 'scroll',
};

/** Выше шапки (1000), моб. Drawer (1002) и плавающих панелей (~100), чтобы список не уходил под футер */
const MKTU_POPUP_Z_INDEX = 1200;

/** Базовая ширина поля (в т.ч. пустого): иначе multiple Select сжимается слишком узко */
const defaultSelectStyle = {
  width: '100px',
  minWidth: 'min(100%, 440px)',
  maxWidth: '100%',
};

const MktuClassSelector = ({
  value,
  onChange,
  mode = 'multiple',
  placeholder = 'Классы МКТУ',
  allowClear = true,
  style,
  size,
  className,
}) => {
  const [searchText, setSearchText] = useState('');
  const [detailClassLabel, setDetailClassLabel] = useState(null);
  const [compactLayout, setCompactLayout] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(COMPACT_MQ).matches
  );

  useEffect(() => {
    const mq = window.matchMedia(COMPACT_MQ);
    const apply = () => setCompactLayout(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  const filtered = useMemo(() => {
    const q = normalize(searchText);
    if (!q) return MKTU_CLASSES;
    const isDigits = /^\d+$/.test(q);
    const terms = q.split(/\s+/).filter(Boolean);
    return MKTU_CLASSES.filter((c) => {
      const code = c.label; // '01', '02', ...
      const desc = normalize(c.description);
      const items = normalize(c.items || ''); // Полный список товаров/услуг
      const belongsTo = normalize(c.belongsTo || '');
      
      if (isDigits) {
        // '1' должно находить '01', '10' — '10', '11', ...
        return code.includes(q.padStart(Math.min(q.length, 2), '0')) || code.startsWith(q);
      }
      // Все слова должны встречаться в описании, товарах/услугах или пояснениях (AND)
      return terms.every((t) => desc.includes(t) || items.includes(t) || belongsTo.includes(t));
    });
  }, [searchText]);

  const detailClass = useMemo(
    () => MKTU_CLASSES.find((x) => x.label === detailClassLabel) || null,
    [detailClassLabel]
  );

  const options = useMemo(() => {
    return filtered.map((c) => {
      const shortDescriptionEl = (
        <span className="mktu-option-desc mktu-option-desc--compact">
          {highlight(firstWords(c.description), searchText)}
        </span>
      );

      const widePreviewEl = (
        <Tooltip
          title={<MktuClassDetailBody c={c} searchText={searchText} />}
          placement="top"
          mouseEnterDelay={0.25}
          getPopupContainer={() => document.body}
          overlayStyle={{
            maxWidth: 'min(520px, 92vw)',
            zIndex: MKTU_POPUP_Z_INDEX + 10,
          }}
          overlayInnerStyle={{
            maxHeight: 'min(72vh, 500px)',
            overflowY: 'auto',
            padding: '4px 2px',
          }}
        >
          <span className="mktu-option-desc mktu-option-desc--wide-preview">
            {highlight(firstWords(c.description, WIDE_PREVIEW_WORDS), searchText)}
          </span>
        </Tooltip>
      );

      return {
        value: c.label,
        label: (
          <div className="mktu-option-row">
            <span style={codeBadgeStyle(c.category)}>{c.label}</span>
            <button
              type="button"
              className="mktu-option-info-btn"
              aria-label={`Подробнее о классе ${c.label}`}
              title="Открыть описание в окне"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDetailClassLabel(c.label);
              }}
            >
              <InfoCircleOutlined />
            </button>
            {compactLayout ? shortDescriptionEl : widePreviewEl}
          </div>
        ),
      };
    });
  }, [filtered, searchText, compactLayout]);

  const handleSearch = useCallback((val) => {
    setSearchText(val);
  }, []);

  const mergedStyle = { ...defaultSelectStyle, ...style };

  const dropdownStyle = useMemo(
    () => ({
      maxWidth: 'min(560px, calc(100vw - 16px))',
      minWidth: compactLayout ? 'min(300px, calc(100vw - 16px))' : 'min(480px, calc(100vw - 16px))',
      width: 'auto',
      zIndex: MKTU_POPUP_Z_INDEX,
    }),
    [compactLayout]
  );

  return (
    <>
      <Select
        className={className ? `mktu-class-selector ${className}` : 'mktu-class-selector'}
        mode={mode}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        allowClear={allowClear}
        style={mergedStyle}
        size={size}
        showSearch
        onSearch={handleSearch}
        filterOption={false}
        options={options}
        optionLabelProp="value" // в теге выбранного значения показываем только код
        dropdownMatchSelectWidth={false}
        popupClassName="mktu-class-selector-dropdown"
        dropdownStyle={dropdownStyle}
        getPopupContainer={() => document.body}
        // см. MKTU_PLACEMENT_HACK — иначе antd/rc-select переворачивает вверх
        placement={MKTU_PLACEMENT_HACK}
        dropdownAlign={MKTU_DROPDOWN_ALIGN}
        notFoundContent={searchText ? 'Нет подходящих классов' : 'Список пуст'}
      />
      <Modal
        title={detailClass ? `Класс ${detailClass.label} · ${detailClass.category}` : ''}
        open={!!detailClass}
        onCancel={() => setDetailClassLabel(null)}
        footer={null}
        width="min(640px, calc(100vw - 24px))"
        destroyOnClose
        zIndex={MKTU_POPUP_Z_INDEX + 50}
        getContainer={() => document.body}
        styles={{ body: { paddingTop: 8 } }}
      >
        {detailClass && <MktuClassDetailBody c={detailClass} searchText={searchText} />}
      </Modal>
    </>
  );
};

export default MktuClassSelector;


